import { useState, useEffect, useRef } from 'react';

/**
 * Typewriter hook — reveals text smoothly character-by-character.
 * Used to smooth out LLM thinking chunks that arrive in big blocks.
 *
 * @param {string} text - The full text to reveal
 * @param {number} speed - Characters per second (default 80)
 * @returns {string} The currently visible portion of the text
 */
export default function useTypewriter(text, speed = 80) {
  const [displayed, setDisplayed] = useState('');
  const targetRef = useRef('');
  const indexRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    targetRef.current = text || '';
  }, [text]);

  useEffect(() => {
    let lastTime = 0;
    const msPerChar = 1000 / speed;

    function tick(timestamp) {
      if (!lastTime) lastTime = timestamp;
      const elapsed = timestamp - lastTime;
      if (elapsed >= msPerChar && indexRef.current < targetRef.current.length) {
        const charsToAdd = Math.max(1, Math.floor(elapsed / msPerChar));
        const end = Math.min(indexRef.current + charsToAdd, targetRef.current.length);
        indexRef.current = end;
        setDisplayed(targetRef.current.slice(0, end));
        lastTime = timestamp;
      }
      if (indexRef.current < targetRef.current.length) {
        rafRef.current = requestAnimationFrame(tick);
      }
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [speed, text]);

  return displayed;
}
